import { z } from "zod";

// A standalone, timestamped note event inside a workout session — the
// "timeline note" (next/timeline-logger.md §9, Solution C). Distinct from:
//   - LogSet.note: a cue on ONE set (stays per-set, untouched);
//   - WorkoutExerciseEntry.notes: the legacy exercise-header note (kept for
//     read-compat, authoring retired).
// A WorkoutNote is its own MOMENT in the session's chronological feed,
// authored via the single chat-style composer and rendered as a note
// mini-card in the timeline (Antenore 2026-06-04: "un solo spazio di
// commento … scrive nel timeline come se avessimo loggato un esercizio").
//
// Stored as its own DDB row (pk=USER#<sub>, sk=WONOTE#<sessionId>#<13-ms>#<id>),
// a per-session time-series — the same keying family as Session / Measurement.
// Listing a session's notes is a begins_with(sk, "WONOTE#<sessionId>#") prefix
// scan in chronological order. Kept OFF the LogSet table ON PURPOSE: a note
// must never reach the set-aggregation path (volume / e1RM / MAX /
// SessionResult / muscle breakdown), so it can't corrupt a training aggregate
// — data integrity is the sole hard gate watch (storage Approach A,
// [[feedback_separation_of_concerns]]).
//
// FUTURE ([[project_jl_timeline_as_feed]]): the timeline generalises to a
// shareable event FEED (athlete / coach / Bob produce + read). `author`
// (optional, defaults to the athlete's own sub at render) is the SEED for that
// — coach/Bob-authored events land in this entity later with NO migration.
// Nothing feed-related is built now; the field is the only cheap insurance.

// Min 4 / max 2000. The 4-char floor rejects an empty/accidental tap (mirrors
// the composer's client-side guard); 2000 is generous for a journal jot.
const NoteText = z.string().trim().min(4).max(2000);

export const WorkoutNote = z.object({
  id: z.uuid(),
  // Partition-key data, included so a single-row read is self-describing.
  // Always equals the row's pk USER#<sub>.
  userSub: z.string().min(1).max(128),
  // The session this note belongs to (sk component). Client-generated UUID,
  // shared with the session envelope.
  sessionId: z.uuid(),
  // When the note was sent (drives the sk + chronological order). Live
  // logging: the wall clock at send. Past-session edit: an in-window tail
  // time (never the live clock — same rule as a set's completedAt, see
  // timelinePlacement.placeCompletedSetAtTail).
  performedAt: z.iso.datetime(),
  text: NoteText,
  // The exercise in focus when the note was sent, if any. Data-only — no UI
  // surface today; helps a future feed read "note while on Back Squat".
  // Catalog slug. Free-standing notes (no focus) omit it.
  exerciseContextId: z.string().min(1).max(64).optional(),
  // Author of the event. Optional; absent = the athlete (the row's userSub).
  // Seed for the coach/Bob feed model — see [[project_jl_timeline_as_feed]].
  author: z.string().min(1).max(128).optional(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
});
export type WorkoutNote = z.infer<typeof WorkoutNote>;

// Client → server payload. Server fills userSub/createdAt/updatedAt. id is
// optional but a UUID when present (offline-first idempotency, same pattern as
// NewSession / NewMeasurement — the client mints it so a replay dedupes).
export const NewWorkoutNote = WorkoutNote.omit({
  id: true,
  userSub: true,
  createdAt: true,
  updatedAt: true,
}).extend({ id: z.uuid().optional() });
export type NewWorkoutNote = z.infer<typeof NewWorkoutNote>;

// Update payload — only `text` is mutable. sessionId + performedAt are sk
// components (immutable after create, like Session.startedAt); changing them
// would resort the key. The route omits them so the client can't author a
// PATCH that asks for the impossible.
export const PatchWorkoutNote = z.object({
  text: NoteText.optional(),
});
export type PatchWorkoutNote = z.infer<typeof PatchWorkoutNote>;
